import React, { useRef, useEffect, useState, useCallback } from "react";
import Anime from "react-anime";
import { useInView } from "react-intersection-observer";
import { animeChildrenProps } from "@helpers/animations";
import { Heading } from "@components/Heading/Heading";
import stylesHeading from "@components/Heading/styles.module.scss";
import TicketCalculatorForm from "@modules/TicketCalculatorV2/components/TicketCalculatorForm";
import ResultCard from "@modules/TicketCalculatorV2/components/ResultCard";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { data } from "./data";
import { spotProps } from "@modules/ArticleSpots/ArticleSpots";
// Road type specific media assets (Danish filenames)
import videoMotorvej from "../../static/ticketCalculatorAssets/motorvej.mp4";
import videoLandevej from "../../static/ticketCalculatorAssets/landevej.mp4";
import videoByzone from "../../static/ticketCalculatorAssets/byzone.mp4";

export interface TicketCalculatorProps {
	imageFolder: string;
	headline?: string;
	randomTexts?: string[];
	spotRibbons?: {
		GreenRibbon: {
			Headline: string;
			Spots: spotProps[];
		}[];
		YellowRibbon: {
			Headline: string;
			Spots: spotProps[];
		}[];
		RedRibbon: {
			Headline: string;
			Spots: spotProps[];
		}[];
	}
}

interface FormData {
	vehicle: string;
	roadType: string;
	conditions: string[];
	speedLimit: number;
	trailers: string;
	roadwork: boolean;
}

const TicketCalculatorV2: React.FC<TicketCalculatorProps> = ({
	imageFolder: _imageFolder,
	headline,
	randomTexts: _randomTexts,
	spotRibbons
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	// Dual video setup for seamless crossfade on future source changes
	const videoARef = useRef<HTMLVideoElement | null>(null);
	const videoBRef = useRef<HTMLVideoElement | null>(null);
	const [activeVideoKey, setActiveVideoKey] = useState<"A" | "B">("A");
	const previousRoadTypeRef = useRef<string | null>(null);
	const [currentSpeed, setCurrentSpeed] = useState<number>(data.defaults.speed);
	const [speedLimit, setSpeedLimit] = useState<{
		maxSpeed: number;
		minSpeed: number;
		availableOptions: number[];
	}>(() => {
		// Initialize with default values from data.ts
		const defaultRoadType = data.roadTypes.find(rt => rt.value === data.defaults.roadType);
		return {
			maxSpeed: data.defaults.speedLimit, 
			minSpeed: defaultRoadType?.speedLimits.min || 40, 
			availableOptions: defaultRoadType?.speedLimits.available || [40, 50, 60, 70, 80, 90]
		};
	});

	const [formData, setFormData] = useState<FormData>({
		vehicle: data.defaults.vehicle,
		roadType: data.defaults.roadType,
		conditions: [],
		speedLimit: data.defaults.speedLimit,
		trailers: '',
		roadwork: data.defaults.roadwork
	});

	const { ref, inView } = useInView({
		threshold: 0.2
	});
	const animateEachChild = {
		...animeChildrenProps,
		...(inView ? { autoplay: true } : { autoplay: false })
	};

	// Helper to get the currently active/inactive video elements
	const getActiveVideo = useCallback((): HTMLVideoElement | null => {
		return activeVideoKey === "A" ? videoARef.current : videoBRef.current;
	}, [activeVideoKey]);

	const getInactiveVideo = useCallback((): HTMLVideoElement | null => {
		return activeVideoKey === "A" ? videoBRef.current : videoARef.current;
	}, [activeVideoKey]);

	// Initialize active video position when ready, based on current speed
	useEffect(() => {
		const video = getActiveVideo();
		if (!video) return;

		const handleCanPlay = () => {
			const maxSpeed = 200;
			const speedPercentage = Math.min(1, Math.max(0, currentSpeed / maxSpeed));
			const videoTime = video.duration ? speedPercentage * video.duration : 0;
			if (!Number.isNaN(videoTime)) {
				video.currentTime = videoTime;
			}
			video.pause();
		};

		video.addEventListener("canplay", handleCanPlay);
		return () => {
			video.removeEventListener("canplay", handleCanPlay);
		};
	}, [currentSpeed, getActiveVideo]);

	// Crossfade to a new source on the inactive video, then swap
	const crossfadeTo = useCallback(async (newSrc: string, crossfadeMs = 600) => {
		const active = getActiveVideo();
		const inactive = getInactiveVideo();
		if (!inactive) return;

		// Derive target progress from current speed (keeps mapping logic centralized)
		const maxSpeed = 200;
		const speedPercentage = Math.min(1, Math.max(0, currentSpeed / maxSpeed));

		// Prepare inactive video
		inactive.style.opacity = "0";
		inactive.src = newSrc;
		inactive.preload = "auto";
		inactive.playsInline = true;
		inactive.muted = true;

		const onMeta = () => {
			inactive.removeEventListener("loadedmetadata", onMeta);
			const targetTime = inactive.duration ? speedPercentage * inactive.duration : 0;
			try {
				if (!Number.isNaN(targetTime)) {
					inactive.currentTime = targetTime;
				}
			} catch { }

			// Perform visual crossfade
			inactive.style.transition = `opacity ${crossfadeMs}ms ease-in-out`;
			if (active) active.style.transition = `opacity ${crossfadeMs}ms ease-in-out`;

			inactive.style.opacity = "1";
			if (active) active.style.opacity = "0";

			window.setTimeout(() => {
				setActiveVideoKey(prev => (prev === "A" ? "B" : "A"));
				// Cleanup old video to free memory
				if (active) {
					active.pause();
					active.removeAttribute("src");
					try { active.load(); } catch { }
					active.style.transition = "";

				}
				inactive.style.transition = "";
			}, crossfadeMs);
		};

		inactive.addEventListener("loadedmetadata", onMeta);
	}, [currentSpeed, getActiveVideo, getInactiveVideo]);

	// Expose crossfade function on the container element for future source changes (keeps linter happy and enables external triggers)
	useEffect(() => {
		if (containerRef.current) {
			(containerRef.current as unknown as { __crossfadeToVideo?: typeof crossfadeTo }).__crossfadeToVideo = crossfadeTo;
		}
	}, [crossfadeTo]);

	// Road type -> media mapping (Danish filenames)
	const getMediaForRoadType = useCallback((roadType: string): { src: string } => {
		switch (roadType) {
			case "city":
				return { src: videoByzone };
			case "highway130":
				return { src: videoMotorvej };
			case "highway110":
				return { src: videoMotorvej };
			case "country":
			default:
				return { src: videoLandevej };
		}
	}, []);

	// When road type changes via first dropdown, crossfade to the corresponding clip
	useEffect(() => {
		if (!formData.roadType) return;
		if (previousRoadTypeRef.current == null) {
			previousRoadTypeRef.current = formData.roadType;
			return;
		}
		if (previousRoadTypeRef.current !== formData.roadType) {
			const media = getMediaForRoadType(formData.roadType);
			crossfadeTo(media.src, 600);
			previousRoadTypeRef.current = formData.roadType;
		}
	}, [formData.roadType, crossfadeTo, getMediaForRoadType]);

	/**
	 * Calculate available speed limits based on vehicle type and road type using data from data.ts
	 */
	const calculateSpeedLimits = useCallback((vehicle: string, roadType: string, trailers: string) => {
		// Get vehicle speed limits from data.ts
		const vehicleLimits = data.vehicleSpeedLimits[vehicle as keyof typeof data.vehicleSpeedLimits];
		if (!vehicleLimits) {
			// Fallback to default values from data.ts
			const defaultRoadType = data.roadTypes.find(rt => rt.value === data.defaults.roadType);
			return { 
				maxSpeed: data.defaults.speedLimit, 
				minSpeed: defaultRoadType?.speedLimits.min || 40, 
				availableOptions: defaultRoadType?.speedLimits.available || [40, 50, 60, 70, 80, 90]
			};
		}

		// Get road type limits
		const roadLimits = vehicleLimits[roadType as keyof typeof vehicleLimits];
		if (!roadLimits) {
			// Fallback to default values from data.ts
			const defaultRoadType = data.roadTypes.find(rt => rt.value === data.defaults.roadType);
			return { 
				maxSpeed: data.defaults.speedLimit, 
				minSpeed: defaultRoadType?.speedLimits.min || 40, 
				availableOptions: defaultRoadType?.speedLimits.available || [40, 50, 60, 70, 80, 90]
			};
		}

		let maxLimit = roadLimits.max;
		let minLimit = roadLimits.min;
		let defaultLimit = roadLimits.default;

		// Check if trailer affects the speed limit
		if (trailers && roadLimits[trailers as keyof typeof roadLimits]) {
			const trailerLimits = roadLimits[trailers as keyof typeof roadLimits] as any;
			maxLimit = trailerLimits.max;
			minLimit = trailerLimits.min;
			defaultLimit = trailerLimits.default;
		}

		// Get available speed limit options from data.ts
		const availableOptions = data.speedLimits
			.map(limit => limit.value)
			.filter(speed => speed >= minLimit && speed <= maxLimit);

		return {
			maxSpeed: defaultLimit,
			minSpeed: minLimit,
			availableOptions
		};
	}, []);

	const updateSpeed = useCallback((speed: number) => {
		setCurrentSpeed(speed);
		const video = getActiveVideo();
		if (!video) return;
		// Update video frame immediately without debouncing for better responsiveness
		if (video.readyState >= 2 && video.duration && !isNaN(video.duration)) {
			const maxSpeed = 200;
			const speedPercentage = Math.min(1, Math.max(0, speed / maxSpeed));
			const videoTime = speedPercentage * video.duration;
			video.currentTime = videoTime;
			video.pause();
		}
	}, [getActiveVideo]);

	const updateSpeedLimit = useCallback(
		(newSpeedLimit: { maxSpeed: number; minSpeed: number }) => {
			setSpeedLimit(prev => ({
				...prev,
				...newSpeedLimit
			}));
		},
		[]
	);

	const updateFormData = useCallback((newFormData: FormData) => {
		setFormData(newFormData);

		// Recalculate speed limits when form data changes
		const newSpeedLimits = calculateSpeedLimits(
			newFormData.vehicle,
			newFormData.roadType,
			newFormData.trailers
		);

		setSpeedLimit(newSpeedLimits);

		// Update speed limit in form data if it's outside the new range
		if (newFormData.speedLimit < newSpeedLimits.minSpeed ||
			newFormData.speedLimit > newSpeedLimits.maxSpeed) {
			setFormData(prev => ({
				...prev,
				speedLimit: newSpeedLimits.maxSpeed
			}));
		}
	}, [calculateSpeedLimits]);

	/**
	 * Determines the card color category based on calculation result
	 */
	const getCardColorCategory = (): string => {
		// Check if speed is over limit
		if (formData.speedLimit >= currentSpeed) {
			return "green"; // No violation
		}

		const pctOver = (currentSpeed / formData.speedLimit) * 100 - 100;
		
		// Simple color categorization - ResultCard handles detailed UBET logic
		// Use threshold from data.ts for consistency
		const yellowThreshold = data.resultCard.rules.displayTexts.klip.percentageOver;
		if (pctOver <= yellowThreshold) {
			return "yellow"; // Minor violation
		} else {
			return "red"; // Major violation
		}
	};

	useEffect(() => {
		(window as any)['dataLayer'] = (window as any)['dataLayer'] || [];
		(window as any)['dataLayer'].push({
			event: 'fine_calculator',
			action: "vist"
		});
	}, []);

	return (
		<div ref={containerRef} className="">
			<section ref={ref} className={styles.ticketcalculator}>
				<div className={styles.ticketcalculatorFormImageMotionBlur}>
					<div className={styles.videoStack}>
						<video
							ref={videoARef}
							src={getMediaForRoadType(formData.roadType).src}
							playsInline
							muted
							preload="auto"
							style={{ opacity: activeVideoKey === "A" ? 1 : 0 }}
						/>
						<video
							ref={videoBRef}
							// Start empty; will be used for crossfades when source changes
							playsInline
							muted
							preload="auto"
							style={{ opacity: activeVideoKey === "B" ? 1 : 0 }}
						/>
					</div>
				</div>

				<div className={styles.contentContainer}>
					<Anime {...animateEachChild}>
						{headline && (
							<div className={styles.addBorder}>
								<Heading
									as="h2"
									center={true}
									className={classNames(
										stylesHeading.headingRibbon,
										styles.pageHeading
									)}
								>
									{headline}
								</Heading>
							</div>
						)}
						<TicketCalculatorForm
							updateSpeed={updateSpeed}
							updateSpeedLimit={updateSpeedLimit}
							updateFormData={updateFormData}
							speedLimitOptions={speedLimit.availableOptions}
							defaultSpeedLimit={speedLimit.maxSpeed}
						/>
					</Anime>
					<ResultCard
						colorCategory={getCardColorCategory()}
						data={{
							speed: currentSpeed,
							speedLimit: formData.speedLimit,
							vehicle: formData.vehicle,
							roadType: formData.roadType,
							conditions: formData.conditions,
							trailers: formData.trailers,
							roadwork: formData.roadwork,
							texts: data.texts.results
						}}
						spotRibbons={spotRibbons}
					/>
				</div>
			</section>
		</div>
	);
};

export { TicketCalculatorV2 };
